/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiSkeletonText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { NormalisedResourceRoleAssignment } from '@modules/role-assignments-lib/types'
import {
  filterItemsByIdOrName,
  specificRoleAssignmentsToTableItems,
} from '@modules/role-assignments-lib'
import RoleAssignmentsFilter from '@modules/role-assignments-components/RoleAssignmentsFilter'
import { CuiAlert } from '@modules/cui/Alert'
import type { useGetCCMClusters as useAdminGetCCMClusters } from '@modules/ccm-admin-lib/hooks'
import type { useGetCCMClusters } from '@modules/ccm-user-lib/hooks'

import CCMRoleAssignmentsTable from './CCMRoleAssignmentsTable'

import type { FunctionComponent } from 'react'

export type MemberOrApiKey = 'member' | 'api-key'

export interface CCMRoleAssignmentsSummaryProps {
  memberOrApiKey: MemberOrApiKey
  roleAssignments: Array<NormalisedResourceRoleAssignment<'cloud-connected'>>
  queryHookResult: ReturnType<typeof useGetCCMClusters | typeof useAdminGetCCMClusters>
}

const CCMRoleAssignmentsSummary: FunctionComponent<CCMRoleAssignmentsSummaryProps> = ({
  memberOrApiKey,
  roleAssignments,
  queryHookResult,
}) => {
  const [filterQuery, setFilterQuery] = useState('')

  const {
    data: ccmClusters,
    isLoading: isLoadingClusters,
    isError: isErrorClusters,
    error: errorClusters,
  } = queryHookResult

  if (isLoadingClusters) {
    return <EuiSkeletonText />
  }

  const items = specificRoleAssignmentsToTableItems(
    'cloud-connected',
    ccmClusters?.items ?? [],
    roleAssignments,
  ).filter(({ roles }) => roles.length !== 0)

  const filteredItems = filterItemsByIdOrName(items, filterQuery)

  return (
    <EuiFlexGroup direction='column' gutterSize='l'>
      {renderError()}

      <EuiFlexItem>
        {memberOrApiKey === 'member' ? (
          <FormattedMessage
            id='roles-assignments.role-assignments-summary.cloud-connected.member'
            defaultMessage='This member has specific access to the following connected clusters:'
          />
        ) : (
          <FormattedMessage
            id='roles-assignments.role-assignments-summary.cloud-connected.api-key'
            defaultMessage='This API key provides access to the following connected clusters:'
          />
        )}
      </EuiFlexItem>

      <EuiFlexItem>
        <RoleAssignmentsFilter onChange={setFilterQuery} />
      </EuiFlexItem>

      <EuiFlexItem>
        <CCMRoleAssignmentsTable items={filteredItems} />
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  function renderError() {
    if (!isErrorClusters) {
      return null
    }

    return (
      <EuiFlexItem>
        <CuiAlert type='danger'>{errorClusters}</CuiAlert>
      </EuiFlexItem>
    )
  }
}

export default CCMRoleAssignmentsSummary
